/* Engagement tracking (FleetView #68/#69): one naming scheme, sent alike to Mixpanel and PostHog.
   Tool-neutral on purpose: dropping a tool = deleting its destination below.

   Every event carries area · page_type · device, plus page_template, page_path, page_id, came_from, environment.
     page_view        once per page; came_from = the click that led here (area.section.element), else internal/external/direct
     ui_click         a button or link; track_id = area.section.element, item = the flavor/product it belongs to
     rage_click       3 clicks within 1s on the same spot
     scroll_depth     25/50/75/100% of the page, once each
     section_reached  a section came on screen (once per section per page)
     section_view     one stretch of 1s+ with the section mostly on screen: seconds, clicks, first (= first stretch on the page)
     gallery_view     one stretch of 0.5s+ on a gallery image: image = its label (the same for every flavor), slide
     panel_view       a drawer, menu or dialog was open 0.5s+: panel, seconds, clicks inside

   Names (CONVENTIONS.md, "Tracking names"):
     area     class track-area-<area> on the section (schema "class"), or data-track-area; default page
     section  class track-<name> on the section (see .github/tracking/section-names.json),
              or data-track-section on a part of a section (gallery, buybox)
     element  data-track="add-to-cart", else the button's own label
     item     data-track-item on an ancestor (flavor handle)
     panel    data-track-panel on a drawer/menu/dialog root
   Nothing runs before Cookiebot "statistics" consent. Never reads form values. */
(() => {
  if (window.MettaTrack) return;
  let config;
  try { config = JSON.parse(document.querySelector('[data-metta-track-config]').textContent); } catch { return; }
  const live = config.themeRole === 'main' && !window.Shopify?.designMode;
  const test = config.themeRole !== 'main' && window.__mettaTrackTest === true;
  if (!live && !test) return;
  const environment = live ? 'live' : 'staging';
  const consent = () => window.Cookiebot?.consent?.statistics === true;
  const slug = value => String(value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40).replace(/-$/, '');
  const suffix = config.templateSuffix || '';
  const pageType = config.template === 'index' ? 'home'
    : config.template === 'product' ? 'pdp'
    : config.template === 'page' ? (/^(lp-|1-vp-)|-lp\b|landing/i.test(suffix) ? 'landing' : /reviews/.test(suffix) ? 'reviews' : 'page')
    : config.template || 'other';
  const pageTemplate = suffix ? `${config.template}.${suffix}` : config.template;
  const pageId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  const device = innerWidth < 768 ? 'mobile' : innerWidth < 1024 ? 'tablet' : 'desktop';

  // Where this page view came from: the click that led here, if it happened within 15s.
  const LAST_CLICK = 'metta_track_last_click';
  let cameFrom = 'direct';
  try {
    const referrer = document.referrer ? new URL(document.referrer) : null;
    if (referrer) cameFrom = referrer.origin === location.origin ? 'internal' : 'external';
    const last = JSON.parse(sessionStorage.getItem(LAST_CLICK) || 'null');
    if (cameFrom === 'internal' && last && Date.now() - last.t < 15000) cameFrom = last.id;
    sessionStorage.removeItem(LAST_CLICK);
  } catch {}

  // --- Destinations -------------------------------------------------------
  const ph = config.posthog || {};
  const destinations = {
    mixpanel: {
      ready: () => !!window.__mettaMixpanelReady && typeof window.mixpanel?.track === 'function' && !window.mixpanel.has_opted_out_tracking?.(),
      send: (name, props, beacon) => window.mixpanel.track(name, props, beacon ? { transport: 'sendBeacon', send_immediately: true } : {}),
    },
    posthog: {
      ready: () => !!window.posthog?.__loaded && !window.posthog.has_opted_out_capturing?.(),
      send: (name, props, beacon) => window.posthog.capture(name, props, beacon ? { transport: 'sendBeacon', send_instantly: true } : undefined),
    },
  };
  if (!ph.key) delete destinations.posthog;
  const queues = Object.fromEntries(Object.keys(destinations).map(name => [name, []]));

  function deliver(dest, event) {
    try {
      if (!destinations[dest].ready()) return false;
      destinations[dest].send(event.name, event.props, event.beacon);
      return true;
    } catch { return false; }
  }
  function enqueue(dest, event) {
    if (deliver(dest, event)) return;
    const queue = queues[dest];
    if (queue.length >= 100) queue.shift();
    queue.push(event);
  }
  function flush() {
    if (!consent()) { Object.values(queues).forEach(queue => { queue.length = 0; }); return; }
    for (const dest in queues) queues[dest].splice(0).forEach(event => enqueue(dest, event));
  }
  function emit(name, props, beacon = false) {
    if (!consent()) return false;
    const event = { name, beacon, props: { area: 'page', ...props, page_type: pageType, page_template: pageTemplate, page_path: location.pathname, page_id: pageId, device, came_from: cameFrom, environment, schema_version: 2 } };
    Object.keys(destinations).forEach(dest => enqueue(dest, event));
    return true;
  }

  function loadPostHog() {
    if (!destinations.posthog || window.__mettaPostHogInit) return;
    window.__mettaPostHogInit = true;
    const script = document.createElement('script');
    script.src = ph.sdk;
    script.async = true;
    script.onload = () => window.posthog?.init(ph.key, {
      api_host: ph.apiHost,
      ui_host: ph.uiHost,
      defaults: '2026-08-30',
      person_profiles: 'identified_only',
      // Same reach as Mixpanel's autocapture, so the month compares like with like.
      autocapture: true,
      capture_pageleave: true,
      enable_heatmaps: true,
      capture_dead_clicks: true,
      // Extensions load from our assets only; anything else PostHog would fetch from its CDN is refused.
      prepare_external_dependency_script: script => {
        const name = (script.src.match(/\/static\/(?:[\d.]+\/)?([a-z-]+)\.js/) || [])[1];
        if (!ph.extensions?.[name]) return null;
        script.src = ph.extensions[name];
        return script;
      },
      loaded: instance => {
        instance.register({ environment, device });
        if (!consent()) instance.opt_out_capturing();
        flush();
      },
    });
    document.head.appendChild(script);
  }

  // --- Naming -------------------------------------------------------------
  const CLICKABLE = '[data-track], a[href], button, [role="button"], input[type="submit"], input[type="button"], summary, select, label';
  const POPUP = '#CybotCookiebotDialog, [class*="kl-private"], .klaviyo-form';

  function classValue(node, prefix) {
    const match = node?.className?.match?.(new RegExp(`(?:^|\\s)${prefix}([a-z0-9-]+)`));
    return match ? match[1] : '';
  }
  function sectionKey(node) {
    if (node.dataset.trackSection || node.dataset.trackPanel) return node.dataset.trackSection || node.dataset.trackPanel;
    const named = classValue(node, 'track-(?!area-)');
    if (named) return named;
    // Unnamed section (check_tracking.py should prevent this): fall back to its type from the id.
    const fromId = slug(node.id.replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '')
      .replace(/_?[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i, '')
      .replace(/_(?=[a-z]*[A-Z0-9])[A-Za-z0-9]{6}$/, ''));
    if (fromId && !/^\d+$/.test(fromId)) return fromId;
    return slug(node.querySelector('[data-section-type]')?.dataset.sectionType) || 'section';
  }
  function sectionOf(el) {
    const node = el.closest('[data-track-section], [data-track-panel], .shopify-section');
    return node ? sectionKey(node) : el.closest(POPUP) ? 'popup' : 'page';
  }
  function areaOf(el) {
    const tagged = el.closest('[data-track-area]');
    if (tagged) return tagged.dataset.trackArea;
    const area = classValue(el.closest('.shopify-section'), 'track-area-');
    if (area) return area;
    return el.closest(POPUP) ? 'popup' : 'page';
  }
  function labelOf(el) {
    const raw = el.dataset.trackLabel || el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('alt')
      || el.querySelector?.('img[alt]')?.alt
      || (el.matches('input[type="submit"], input[type="button"]') ? el.value : '')
      || (el.matches('select') ? el.name : '')
      || el.textContent;
    const text = String(raw || '').replace(/\s+/g, ' ').trim();
    return text.length > 80 ? '' : text;
  }
  function describe(el) {
    const label = labelOf(el);
    const area = areaOf(el);
    const section = sectionOf(el);
    const tagged = el.closest('[data-track]')?.dataset.track;
    const element = tagged && !tagged.includes('.') ? tagged : slug(label) || el.tagName.toLowerCase();
    const props = { track_id: tagged?.includes('.') ? tagged : `${area}.${section}.${element}`, area, section, element, label };
    const item = el.closest('[data-track-item]')?.dataset.trackItem;
    const panel = el.closest('[data-track-panel]')?.dataset.trackPanel;
    if (item) props.item = item;
    if (panel) props.panel = panel;
    return props;
  }
  function targetOf(link) {
    try { const url = new URL(link.href, location.href); return url.origin === location.origin ? url.pathname : url.hostname; } catch { return ''; }
  }

  // --- Clicks and rage clicks --------------------------------------------
  let burst = null;
  document.addEventListener('click', event => {
    if (!consent() || !(event.target instanceof Element)) return;
    const el = event.target.closest(CLICKABLE);
    // Rage: three clicks within a second, within 30px, on anything (also on things that aren't buttons).
    const near = burst && event.timeStamp - burst.start < 1000 && Math.abs(event.clientX - burst.x) < 30 && Math.abs(event.clientY - burst.y) < 30;
    if (near) burst.count++;
    else burst = { start: event.timeStamp, x: event.clientX, y: event.clientY, count: 1 };
    if (burst.count === 3) emit('rage_click', describe(el || event.target));
    stints.forEach((stint, node) => { if (stint.since && node.contains(event.target)) stint.clicks++; });
    panels.forEach((panel, node) => { if (panel.since && node.contains(event.target)) panel.clicks++; });
    if (!el) return;
    const props = describe(el);
    const link = el.matches('a[href]') ? el : null;
    if (link) props.target_path = targetOf(link);
    try { sessionStorage.setItem(LAST_CLICK, JSON.stringify({ id: props.track_id, t: Date.now() })); } catch {}
    emit('ui_click', props, !!link);
    requestAnimationFrame(checkPanels);
  }, { capture: true, passive: true });

  // --- Scroll depth -------------------------------------------------------
  const depthsSent = new Set();
  let depthFrame = 0;
  function checkDepth() {
    depthFrame = 0;
    if (!consent()) return;
    const height = Math.max(document.documentElement.scrollHeight, 1);
    const percent = Math.min(100, (scrollY + innerHeight) / height * 100);
    [25, 50, 75, 100].forEach(depth => {
      if (percent >= depth - 1 && !depthsSent.has(depth) && emit('scroll_depth', { depth })) depthsSent.add(depth);
    });
  }
  addEventListener('scroll', () => { depthFrame ||= requestAnimationFrame(checkDepth); }, { passive: true });

  // --- Time on sections and gallery images -------------------------------
  // A stint = one uninterrupted stretch on screen. Each stint of 1s+ (0.5s+ for images) is one event,
  // so reach = distinct page_id per section and time = sum of seconds.
  const stints = new Map();
  let sectionObserver, slideObserver, scanTimer;
  function start(node) {
    const stint = stints.get(node);
    if (stint && stint.visible && !stint.since && document.visibilityState === 'visible' && consent()) { stint.since = performance.now(); stint.clicks = 0; }
  }
  function end(node, beacon = false) {
    const stint = stints.get(node);
    if (!stint?.since) return;
    const seconds = (performance.now() - stint.since) / 1000;
    stint.since = 0;
    if (seconds < stint.min) return;
    const props = { ...stint.props, seconds: Math.round(seconds * 10) / 10 };
    if (stint.event === 'section_view') Object.assign(props, { clicks: stint.clicks, first: !stint.viewed });
    if (emit(stint.event, props, beacon)) stint.viewed = true;
  }
  function update(entry, visible) {
    const stint = stints.get(entry.target);
    if (!stint) return;
    // Empty wrappers (sections switched off by a setting) count as reached at height 0; skip them.
    if (entry.isIntersecting && entry.intersectionRect.height > 0 && !stint.reached && stint.event === 'section_view' && emit('section_reached', stint.props)) stint.reached = true;
    stint.visible = visible;
    visible ? start(entry.target) : end(entry.target);
  }
  function watch(node, observer, event, min, props) {
    if (stints.has(node)) return;
    stints.set(node, { event, min, props, visible: false, since: 0, clicks: 0 });
    observer.observe(node);
  }
  function slideLabel(node) {
    const img = node.querySelector('img');
    return slug(node.dataset.trackLabel || node.dataset.label || img?.alt || (img?.currentSrc || img?.src || '').split('/').pop().split('?')[0]) || 'image';
  }
  function scan() {
    if (!consent() || !window.IntersectionObserver) return;
    // Tall sections never reach 50% of themselves, so "on screen" = half the section or half the viewport.
    sectionObserver ||= new IntersectionObserver(entries => entries.forEach(entry => {
      const height = entry.intersectionRect.height;
      update(entry, height > 0 && height >= Math.min(entry.boundingClientRect.height, innerHeight) * 0.5);
    }), { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
    slideObserver ||= new IntersectionObserver(entries => entries.forEach(entry => update(entry, entry.intersectionRatio >= 0.6)), { threshold: [0, 0.6, 1] });
    stints.forEach((stint, node) => { if (!node.isConnected) { end(node); stints.delete(node); } });
    // Only the page body and footer: header, cart and popups are on screen whenever they're open (panel_view covers them).
    const wrappers = [...document.querySelectorAll('.shopify-section')].filter(node => ['page', 'footer'].includes(areaOf(node)));
    wrappers.forEach((node, i) => {
      const index = i + 1;
      watch(node, sectionObserver, 'section_view', 1, { area: areaOf(node), section: sectionKey(node), section_index: index });
      node.querySelectorAll('[data-track-section]').forEach(part => {
        if (!part.closest('[data-track-area]')) watch(part, sectionObserver, 'section_view', 1, { area: areaOf(part), section: sectionKey(part), section_index: index });
      });
    });
    document.querySelectorAll('[data-track-slide], [data-v2-slide], .product-single__media-slide').forEach(node => {
      const gallery = node.closest('[data-track-gallery], [data-v2-gallery]');
      watch(node, slideObserver, 'gallery_view', 0.5, {
        area: areaOf(node),
        section: sectionOf(node),
        gallery: gallery?.dataset.trackGallery || gallery?.dataset.v2Gallery || sectionOf(node),
        slide: [...node.parentElement.children].indexOf(node) + 1,
        image: slideLabel(node),
      });
    });
    checkPanels();
  }

  // --- Panels (drawers, menus, dialogs) -----------------------------------
  // Menus here hide with visibility:hidden, which IntersectionObserver can't see, so open = laid out on screen and visible.
  const panels = new Map();
  let panelFrame = 0;
  function isOpen(node) {
    if (!node.isConnected || document.visibilityState !== 'visible') return false;
    const rect = node.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2 || rect.right <= 0 || rect.bottom <= 0 || rect.left >= innerWidth || rect.top >= innerHeight) return false;
    if (node.checkVisibility) return node.checkVisibility({ visibilityProperty: true, opacityProperty: true });
    const style = getComputedStyle(node);
    return style.visibility !== 'hidden' && style.opacity !== '0';
  }
  function closePanel(node, panel, beacon = false) {
    const seconds = (performance.now() - panel.since) / 1000;
    panel.since = 0;
    if (seconds >= 0.5) emit('panel_view', { area: areaOf(node), panel: node.dataset.trackPanel, ...(node.dataset.trackItem ? { item: node.dataset.trackItem } : {}), seconds: Math.round(seconds * 10) / 10, clicks: panel.clicks }, beacon);
  }
  function checkPanels(beacon = false) {
    panelFrame = 0;
    if (!consent()) return;
    document.querySelectorAll('[data-track-panel]').forEach(node => { if (!panels.has(node)) panels.set(node, { since: 0, clicks: 0 }); });
    panels.forEach((panel, node) => {
      const open = beacon !== true && isOpen(node);
      if (open && !panel.since) { panel.since = performance.now(); panel.clicks = 0; }
      if (!open && panel.since) closePanel(node, panel, beacon === true);
      if (!node.isConnected) panels.delete(node);
    });
  }
  const schedulePanels = () => { panelFrame ||= requestAnimationFrame(() => checkPanels()); };
  document.addEventListener('transitionend', schedulePanels, { capture: true, passive: true });

  document.addEventListener('visibilitychange', () => {
    const hidden = document.visibilityState === 'hidden';
    stints.forEach((stint, node) => hidden ? end(node, true) : start(node));
    checkPanels(hidden);
  });
  addEventListener('pagehide', () => { stints.forEach((stint, node) => end(node, true)); checkPanels(true); });

  // --- Consent ------------------------------------------------------------
  let pageViewSent = false;
  let domObserver;
  function sync() {
    if (!consent()) {
      stints.forEach(stint => { stint.since = 0; });
      panels.forEach(panel => { panel.since = 0; });
      domObserver?.disconnect();
      if (window.posthog?.__loaded) window.posthog.opt_out_capturing();
      flush();
      return;
    }
    loadPostHog();
    // New content (lazy galleries, drawers) gets watched within half a second; attribute changes only re-check panels.
    // Started on consent only, so visitors who haven't said yes pay for nothing but this file.
    domObserver ||= new MutationObserver(mutations => {
      if (!scanTimer && mutations.some(mutation => mutation.type === 'childList')) scanTimer = setTimeout(() => { scanTimer = 0; scan(); }, 500);
      schedulePanels();
    });
    domObserver.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'open', 'aria-hidden'] });
    if (window.posthog?.__loaded && window.posthog.has_opted_out_capturing()) window.posthog.opt_in_capturing({ captureEventName: false });
    try { if (window.__mettaMixpanelReady) window.mixpanel.register({ device }); } catch {}
    if (!pageViewSent) pageViewSent = emit('page_view', { referrer_host: (() => { try { return document.referrer ? new URL(document.referrer).hostname : ''; } catch { return ''; } })() });
    flush();
    scan();
    checkDepth();
    stints.forEach((stint, node) => start(node));
  }
  ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline', 'metta:mixpanel-ready'].forEach(name => window.addEventListener(name, sync));

  window.MettaTrack = {
    // For other theme code (v2-measurement) that already sends to Mixpanel itself.
    toPostHog: (name, props, beacon = false) => { if (destinations.posthog && consent()) enqueue('posthog', { name, props: { ...props, environment, device }, beacon }); },
    status: () => ({ environment, device, pageType, cameFrom, consent: consent(), ready: Object.fromEntries(Object.keys(destinations).map(dest => [dest, destinations[dest].ready()])), queued: Object.fromEntries(Object.entries(queues).map(([dest, queue]) => [dest, queue.length])) }),
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true }); else sync();
})();
